import React, { useState } from "react";
import { submitSubscribeForm } from "@/core/Forms/SubscribeForm/form.actions";
import { SubscribeFormData } from "@/core/Forms/SubscribeForm/types";

const BlogNewsletter: React.FC = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({
    isSubmitting: false,
    isSuccess: false,
    error: null as string | null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setState((prev) => ({
        ...prev,
        error: "Please enter your email address",
      }));
      return;
    }

    setState((prev) => ({ ...prev, isSubmitting: true, error: null }));

    try {
      const formData: SubscribeFormData = {
        email: email.trim().toLowerCase(),
        source: "blog_sidebar",
      };

      const response = await submitSubscribeForm(formData);

      if (response.success) {
        setState({
          isSubmitting: false,
          isSuccess: true,
          error: null,
        });
        setEmail("");

        // Reset success state after 5 seconds
        setTimeout(() => {
          setState((prev) => ({ ...prev, isSuccess: false }));
        }, 5000);
      } else {
        setState({
          isSubmitting: false,
          isSuccess: false,
          error: response.message || "Subscription failed. Please try again.",
        });
      }
    } catch (error) {
      setState({
        isSubmitting: false,
        isSuccess: false,
        error: "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <div className="bg-gradient-to-br from-cannabis-green-900 to-legal-blue-900 text-white rounded-xl shadow-md p-5 border border-justice-gold-500/20">
      <h3 className="font-bold text-xl mb-2 flex items-center">
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
        Cannabis News & Updates
      </h3>
      <p className="text-white/90 mb-4 text-sm leading-relaxed">
        Get the latest on strains, products, and DC cannabis events delivered to
        your inbox. No spam, just the good stuff.
      </p>

      {state.isSuccess ? (
        <div className="bg-white/20 rounded-lg p-4 text-center animate-pulse">
          <svg
            className="w-8 h-8 text-white/90 mx-auto mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <p className="text-white font-medium">Thanks for subscribing!</p>
          <p className="text-white/80 text-sm mt-1">
            Check your inbox soon for exclusive cannabis content.
          </p>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={handleSubmit}>
          <div>
            <div className="relative">
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg p-3 pl-10 text-gray-800 text-sm bg-white/90 border border-transparent focus:border-justice-gold-400 focus:ring-1 focus:ring-justice-gold-400 focus:outline-none shadow-inner"
                required
              />
              <svg
                className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                />
              </svg>
            </div>
            {state.error && (
              <p className="text-yellow-100 text-xs mt-1.5 flex items-center">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                {state.error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={state.isSubmitting}
            className="w-full bg-justice-gold-500 hover:bg-justice-gold-400 text-gray-900 py-2.5 rounded-lg font-medium transition-colors duration-300 disabled:opacity-70 shadow-md flex justify-center items-center"
          >
            {state.isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-900"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Subscribing...
              </>
            ) : (
              "Subscribe"
            )}
          </button>

          <p className="text-xs text-gray-300 text-center">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </form>
      )}
    </div>
  );
};

export default BlogNewsletter;
